import type { TestResult } from '@/lib/supabase';
import { literacyTopPercentile } from '@/lib/percentile';
import { careerTracks } from '@/mocks/careerData';
import { iqCategoryMeta } from '@/mocks/iqTestData';
import { vocabTypeMeta } from '@/mocks/vocabularyQuestions';
import { readingTypeMeta } from '@/mocks/readingQuestions';
import { mbtiProfiles } from '@/mocks/studyStyleData';
import { englishLevelMeta } from '@/mocks/englishWords';
import { englishTypeMeta } from '@/lib/englishQuestions';
import { analyzeComposite, buildLearningRadarChart, type CompositeInput } from '@/lib/compositeAnalysis';

export interface StudentSummary {
  id: string;
  name: string;
  grade: string | null;
  school: string | null;
}

const TEST_TYPE_LABEL: Record<string, string> = {
  career: '진로·계열·직업 탐색',
  iq: 'IQ 테스트',
  literacy: '어휘력·문해력 검사',
  study_style: '성격·성향 기반 공부법 검사',
  english: '영어 단어 검사',
};

const HOLLAND_LABEL: Record<string, string> = {
  R: '현실형', I: '탐구형', A: '예술형', S: '사회형', E: '진취형', C: '관습형',
};

const VARK_LABEL: Record<string, string> = {
  V: '시각형', A: '청각형', R: '읽기형', K: '체험형',
};

const VALUE_LABEL: Record<string, string> = {
  achievement: '성취감',
  helping: '사회공헌',
  creativity: '창의·표현',
  intellectual: '지적 탐구',
  leadership: '영향력',
  stability: '안정·전문성',
};

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function formatTopPercentile(p: number): string {
  if (p >= 10) return `${Math.round(p)}`;
  if (p >= 1) return p.toFixed(1);
  return p.toFixed(2);
}

function barRow(label: string, correct: number, total: number): string {
  const pct = total > 0 ? Math.round((correct / total) * 100) : 0;
  return `
    <div class="bar-row">
      <span class="bar-label">${escapeHtml(label)}</span>
      <span class="bar-track"><span class="bar-fill" style="width:${pct}%"></span></span>
      <span class="bar-val">${correct}/${total}</span>
    </div>
  `;
}

function chip(text: string, tone: 'green' | 'blue' | 'gray' = 'gray'): string {
  const cls = tone === 'green' ? 'chip chip-green' : tone === 'blue' ? 'chip chip-blue' : 'chip';
  return `<span class="${cls}">${escapeHtml(text)}</span>`;
}

function detailCard(title: string, date: string, inner: string): string {
  return `
    <div class="detail-card">
      <div class="detail-head">
        <span class="detail-title">${escapeHtml(title)}</span>
        <span class="detail-date">${date}</span>
      </div>
      ${inner}
    </div>
  `;
}

/* ─────────── IQ 결과 ─────────── */
function renderIq(result: Record<string, unknown>, score: number | null, date: string): string {
  const iq = score ?? (typeof result.scaled === 'number' ? Math.round(result.scaled as number) : null);
  const percentile = typeof result.percentile === 'number' ? (result.percentile as number) : null;
  const correctCount = typeof result.correctCount === 'number' ? (result.correctCount as number) : null;
  const range = Array.isArray(result.iqRange) ? (result.iqRange as number[]) : null;
  const categoryScores = (result.categoryScores ?? {}) as Record<string, { correct: number; total: number }>;

  let inner = '';
  if (iq != null) {
    inner += `<div class="iq-score">${iq}</div>`;
  }
  if (range && range.length === 2) {
    inner += `<div class="detail-meta">추정 범위 ${range[0]} – ${range[1]}</div>`;
  }
  if (percentile != null) {
    const isTop = percentile <= 50;
    inner += `<div class="detail-meta">${isTop ? '상위' : '하위'} <strong>${formatTopPercentile(percentile)}%</strong></div>`;
  }
  if (correctCount != null) {
    inner += `<div class="detail-meta">정답 문항 수 <strong>${correctCount} / 100</strong></div>`;
  }

  if (Object.keys(categoryScores).length > 0) {
    inner += `<div class="detail-sub">영역별 분석</div>`;
    iqCategoryMeta.forEach((cat) => {
      const s = categoryScores[cat.id];
      if (s) inner += barRow(cat.label, s.correct, s.total);
    });
  }

  return detailCard('IQ 종합 인지능력', date, inner);
}

/* ─────────── 진로·계열 결과 ─────────── */
function renderCareer(result: Record<string, unknown>, date: string): string {
  const topHolland = Array.isArray(result.topHolland) ? (result.topHolland as string[]) : [];
  const topValues = Array.isArray(result.topValues) ? (result.topValues as string[]) : [];
  const trackIds = Array.isArray(result.recommendedTrackIds) ? (result.recommendedTrackIds as string[]) : [];
  const majors = Array.isArray(result.recommendedMajors) ? (result.recommendedMajors as string[]) : [];
  const jobs = Array.isArray(result.allJobs) ? (result.allJobs as string[]) : [];

  const hollandNames = topHolland.map((c) => HOLLAND_LABEL[c] || c).filter(Boolean);
  const valueNames = topValues.map((v) => VALUE_LABEL[v] || v).filter(Boolean);
  const trackNames = trackIds
    .map((id) => careerTracks.find((t) => t.id === id)?.name)
    .filter((n): n is string => Boolean(n));

  let inner = '';

  if (hollandNames.length > 0) {
    inner += `<div class="detail-sub">나의 흥미 유형</div>`;
    inner += `<div class="chips">${hollandNames.map((n) => chip(n, 'blue')).join('')}</div>`;
  }

  if (valueNames.length > 0) {
    inner += `<div class="detail-sub">나의 직업 가치관</div>`;
    inner += `<div class="chips">${valueNames.map((n) => chip(n, 'gray')).join('')}</div>`;
  }

  if (trackNames.length > 0) {
    inner += `<div class="detail-sub">추천 계열</div>`;
    inner += `<div class="chips">${trackNames.map((n) => chip(n, 'green')).join('')}</div>`;
  }

  if (majors.length > 0) {
    inner += `<div class="detail-sub">추천 전공학과</div>`;
    inner += `<div class="chips">${majors.slice(0, 8).map((n) => chip(n, 'blue')).join('')}</div>`;
  }

  if (jobs.length > 0) {
    inner += `<div class="detail-sub">추천 직업</div>`;
    inner += `<div class="chips">${jobs.slice(0, 10).map((n) => chip(n, 'gray')).join('')}</div>`;
  }

  return detailCard('진로·계열·직업 탐색 결과', date, inner);
}

/* ─────────── 어휘력·문해력 결과 ─────────── */
function renderLiteracy(result: Record<string, unknown>, score: number | null, date: string): string {
  const totalCorrect = score ?? (typeof result.totalCorrect === 'number' ? (result.totalCorrect as number) : null);
  const totalQuestions = typeof result.totalQuestions === 'number' ? (result.totalQuestions as number) : null;
  const vocabPct = typeof result.vocabPct === 'number' ? (result.vocabPct as number) : null;
  const readingPct = typeof result.readingPct === 'number' ? (result.readingPct as number) : null;
  const totalPct = typeof result.totalPct === 'number'
    ? (result.totalPct as number)
    : totalCorrect != null && totalQuestions ? Math.round((totalCorrect / totalQuestions) * 100) : null;
  const vocabScores = (result.vocabScores ?? {}) as Record<string, { correct: number; total: number }>;
  const readingScores = (result.readingScores ?? {}) as Record<string, { correct: number; total: number }>;

  let inner = '';
  if (totalCorrect != null) {
    inner += `
      <div class="lit-score">${totalCorrect}</div>
      <div class="detail-meta">전체 <strong>${totalQuestions ?? '—'}</strong>문항 중 정답</div>
    `;
  }
  if (totalPct != null) {
    const topPct = literacyTopPercentile(totalPct);
    const isTop = totalPct >= 60;
    inner += `<div class="detail-meta">전체 백분위 <strong>${isTop ? '상위' : '하위'} ${formatTopPercentile(topPct)}%</strong></div>`;
  }
  if (vocabPct != null || readingPct != null) {
    inner += `<div class="chips chips-center">`;
    if (vocabPct != null) inner += chip(`어휘력 ${vocabPct}%`, 'blue');
    if (readingPct != null) inner += chip(`문해력 ${readingPct}%`, 'green');
    inner += `</div>`;
  }

  if (Object.keys(vocabScores).length > 0) {
    inner += `<div class="detail-sub">어휘력 유형별 정답률</div>`;
    vocabTypeMeta.forEach((cat) => {
      const s = vocabScores[cat.id];
      if (s) inner += barRow(cat.label, s.correct, s.total);
    });
  }

  if (Object.keys(readingScores).length > 0) {
    inner += `<div class="detail-sub">문해력 유형별 정답률</div>`;
    readingTypeMeta.forEach((cat) => {
      const s = readingScores[cat.id];
      if (s) inner += barRow(cat.label, s.correct, s.total);
    });
  }

  return detailCard('어휘력·문해력 검사 결과', date, inner);
}

/* ─────────── 영어 단어 결과 ─────────── */
function renderEnglish(result: Record<string, unknown>, score: number | null, date: string): string {
  const totalCorrect = score ?? (typeof result.totalCorrect === 'number' ? (result.totalCorrect as number) : null);
  const totalQuestions = typeof result.totalQuestions === 'number' ? (result.totalQuestions as number) : null;
  const totalPct = typeof result.totalPct === 'number' ? (result.totalPct as number) : null;
  const percentile = typeof result.percentile === 'number' ? (result.percentile as number) : null;
  const level = typeof result.level === 'string' ? (result.level as string) : '';
  const typeScores = (result.typeScores ?? {}) as Record<string, { correct: number; total: number }>;

  const levelLabel = englishLevelMeta.find((l) => l.id === level)?.label || '영어 단어';

  let inner = '';
  if (totalCorrect != null) {
    inner += `
      <div class="lit-score">${totalCorrect}</div>
      <div class="detail-meta">전체 <strong>${totalQuestions ?? '—'}</strong>문항 중 정답 · ${levelLabel}</div>
    `;
  }
  if (percentile != null) {
    const isTop = percentile <= 50;
    inner += `<div class="detail-meta">전체 백분위 <strong>${isTop ? '상위' : '하위'} ${formatTopPercentile(percentile)}%</strong></div>`;
  }
  if (totalPct != null) {
    inner += `<div class="chips chips-center">${chip(`종합 정답률 ${totalPct}%`, 'green')}</div>`;
  }
  if (Object.keys(typeScores).length > 0) {
    inner += `<div class="detail-sub">유형별 정답률</div>`;
    englishTypeMeta.forEach((cat) => {
      const s = typeScores[cat.id];
      if (s) inner += barRow(cat.label, s.correct, s.total);
    });
  }

  return detailCard(`${levelLabel} 검사 결과`, date, inner);
}

/* ─────────── 공부법 결과 ─────────── */
function renderStudyStyle(result: Record<string, unknown>, date: string): string {
  const vark = typeof result.vark === 'string' ? (result.vark as string) : '';
  const mbti = typeof result.mbti === 'string' ? (result.mbti as string) : '';
  const mbtiProfile = mbti ? mbtiProfiles[mbti] : null;

  let inner = '';
  if (vark) {
    inner += `<div class="detail-sub">나의 학습 유형 (VARK)</div>`;
    inner += `<div class="chips">${chip(`${vark} · ${VARK_LABEL[vark] || ''}`, 'green')}</div>`;
  }
  if (mbti) {
    inner += `<div class="detail-sub">나의 MBTI 성격 유형</div>`;
    inner += `<div class="chips">${chip(`${mbti} · ${mbtiProfile?.name || ''}`, 'blue')}</div>`;
    if (mbtiProfile?.description) {
      inner += `<p class="detail-desc">${escapeHtml(mbtiProfile.description)}</p>`;
    }
  }

  return detailCard('성격·성향 기반 공부법 검사 결과', date, inner);
}

/* ─────────── 기타 결과 ─────────── */
function renderGeneric(r: TestResult, date: string): string {
  const label = TEST_TYPE_LABEL[r.test_type] || r.test_type;
  let inner = `<div class="detail-desc">${escapeHtml(r.title || label)}</div>`;
  if (r.score != null) {
    inner += `<div class="detail-meta">점수 <strong>${r.score}</strong>점</div>`;
  }
  return detailCard(`${label} 결과`, date, inner);
}

function renderResult(r: TestResult): string {
  const date = formatDate(r.created_at);
  const result = r.result ?? {};
  switch (r.test_type) {
    case 'iq':
      return renderIq(result, r.score, date);
    case 'career':
      return renderCareer(result, date);
    case 'literacy':
      return renderLiteracy(result, r.score, date);
    case 'english':
      return renderEnglish(result, r.score, date);
    case 'study_style':
      return renderStudyStyle(result, date);
    default:
      return renderGeneric(r, date);
  }
}

/* ─────────── 종합 융합 분석 (인쇄용) ─────────── */

const TYPE_ORDER = ['literacy', 'iq', 'english', 'study_style', 'career'];

function radarSvgString(subjects: { name: string; level: number }[]): string {
  const chart = buildLearningRadarChart(subjects);
  const gridEls = chart.grids
    .map((pts) => `<polygon points="${pts}" fill="none" stroke="#cbd5e1" stroke-width="1" />`)
    .join('');
  const axisEls = chart.axes
    .map((a) => `<line x1="${chart.centerX}" y1="${chart.centerY}" x2="${a.axisX}" y2="${a.axisY}" stroke="#cbd5e1" stroke-width="1" />`)
    .join('');
  const dots = chart.axes
    .map((a) => `<circle cx="${a.dataX.toFixed(1)}" cy="${a.dataY.toFixed(1)}" r="3.5" fill="#00695c" />`)
    .join('');
  const labels = chart.axes
    .map((a) => `<text x="${a.labelX.toFixed(1)}" y="${a.labelY.toFixed(1)}" text-anchor="${a.labelAnchor}" dominant-baseline="central" font-size="12" fill="#334155" font-weight="600">${escapeHtml(a.label)}</text>`)
    .join('');

  return `
    <div class="radar-wrap">
      <svg viewBox="0 0 ${chart.viewWidth} ${chart.viewHeight}" class="radar-svg">
        ${gridEls}
        ${axisEls}
        <polygon points="${chart.dataPolygon}" fill="#00695c" fill-opacity="0.15" stroke="#00695c" stroke-width="2" />
        ${dots}
        ${labels}
      </svg>
    </div>
  `;
}

function renderComposite(input: CompositeInput): string {
  const a = analyzeComposite(input);
  if (!a.hasAllResults) return '';

  const tags = a.summaryTags.length
    ? `<div class="chips chips-center">${a.summaryTags.map((t) => chip(t, 'blue')).join('')}</div>`
    : '';

  const subjectChips = a.academicAbility.subjects
    .map((s) => chip(`${s.name} · ${s.levelLabel}`, 'gray'))
    .join('');

  const strengths = a.subjectAnalysis.strengths.map((s) => chip(s.name, 'green')).join('');
  const weaknesses = a.subjectAnalysis.weaknesses.map((s) => chip(s.name, 'gray')).join('');
  const strategies = a.weaknessRemedy.strategies
    .map((s) => `<div class="composite-li">· ${escapeHtml(s)}</div>`)
    .join('');

  return `
    <div class="detail-card composite">
      <div class="detail-head">
        <span class="detail-title">🔮 종합 융합 분석 결과</span>
      </div>
      <p class="composite-desc">${escapeHtml(a.finalRecommendation.description)}</p>
      ${tags}
      <div class="detail-sub">학습역량 육각형 분석</div>
      ${radarSvgString(a.academicAbility.subjects)}
      <p class="composite-summary">${escapeHtml(a.academicAbility.summary)}</p>
      <div class="chips chips-center">${subjectChips}</div>
      <div class="detail-sub">추천 계열</div>
      <div class="chips">${a.finalRecommendation.careerTracks.map((t) => chip(t, 'green')).join('')}</div>
      <div class="detail-sub">추천 전공</div>
      <div class="chips">${a.finalRecommendation.majors.slice(0, 5).map((m) => chip(m, 'blue')).join('')}</div>
      <div class="detail-sub">추천 직업</div>
      <div class="chips">${a.finalRecommendation.jobs.slice(0, 5).map((j) => chip(j, 'gray')).join('')}</div>
      <div class="detail-sub">강점 과목</div>
      <div class="chips">${strengths}</div>
      <div class="detail-sub">보완이 필요한 과목</div>
      <div class="chips">${weaknesses}</div>
      <div class="detail-sub">약점 과목 극복 전략</div>
      <div class="composite-strategies">${strategies}</div>
      <div class="composite-study">${escapeHtml(a.weaknessRemedy.studyMethod)}</div>
    </div>
  `;
}

export function generatePrintHtml(
  results: TestResult[],
  student: StudentSummary,
  compositeInput?: CompositeInput,
): string {
  const sorted = [...results].sort((a, b) => {
    const ai = TYPE_ORDER.indexOf(a.test_type);
    const bi = TYPE_ORDER.indexOf(b.test_type);
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  const compositeHtml = compositeInput ? renderComposite(compositeInput) : '';

  const bodyContent = `
    <div class="header">
      <div class="avatar">${escapeHtml(student.name.charAt(0))}</div>
      <div>
        <div class="name">${escapeHtml(student.name)}</div>
        <div class="meta">${escapeHtml(student.grade || '학년 미정')} · ${escapeHtml(student.school || '학교 미기입')}</div>
      </div>
    </div>
    <div class="total">선택한 검사 결과 <strong>${sorted.length}</strong>건</div>
    ${sorted.map((r) => renderResult(r)).join('')}
    ${compositeHtml}
  `;

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    @page { margin: 20px; }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Noto Sans KR', 'Apple SD Gothic Neo', 'Malgun Gothic', sans-serif;
      background: #ffffff !important;
      color: #111111 !important;
      padding: 20px;
      orphans: 3;
      widows: 3;
    }
    .header { display: flex; align-items: center; gap: 12px; margin-bottom: 20px; break-inside: avoid; page-break-inside: avoid; }
    .avatar { width: 48px; height: 48px; border-radius: 50%; background: #00695c; color: #fff; display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 18px; }
    .name { font-size: 20px; font-weight: 700; margin-bottom: 4px; }
    .meta { font-size: 14px; color: #555; }
    .total { font-size: 14px; color: #555; margin-bottom: 16px; }

    .detail-card { border: 1px solid #e0e0e0; border-radius: 12px; padding: 18px; margin-bottom: 16px; background: #fafafa; break-inside: avoid; page-break-inside: avoid; }
    .detail-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px; padding-bottom: 10px; border-bottom: 1px solid #e0e0e0; }
    .detail-title { font-weight: 700; font-size: 15px; color: #111; }
    .detail-date { font-size: 12px; color: #777; white-space: nowrap; }

    .iq-score { font-size: 44px; font-weight: 800; color: #00695c; line-height: 1; }
    .iq-badge { display: inline-block; font-size: 13px; font-weight: 700; color: #00695c; background: #e0f2f1; border: 1px solid #b2dfdb; padding: 3px 12px; border-radius: 999px; margin: 8px 0 4px; }
    .lit-score { font-size: 40px; font-weight: 800; color: #00695c; line-height: 1; }
    .detail-meta { font-size: 13px; color: #555; margin-top: 6px; }
    .detail-desc { font-size: 13px; color: #444; line-height: 1.6; margin-top: 4px; }
    .detail-sub { font-size: 12px; font-weight: 700; color: #444; margin-top: 14px; margin-bottom: 6px; break-after: avoid; page-break-after: avoid; }

    .chips { display: flex; flex-wrap: wrap; gap: 6px; break-inside: avoid; page-break-inside: avoid; }
    .chips-center { justify-content: center; margin-top: 8px; }
    .chip { font-size: 12px; background: #f1f3f5; color: #333; border: 1px solid #e0e0e0; padding: 3px 10px; border-radius: 999px; }
    .chip-green { background: #e0f2f1; color: #00695c; border-color: #b2dfdb; }
    .chip-blue { background: #e3f2fd; color: #1565c0; border-color: #bbdefb; }

    .bar-row { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; break-inside: avoid; page-break-inside: avoid; }
    .bar-label { font-size: 12px; color: #444; width: 96px; flex-shrink: 0; }
    .bar-track { flex: 1; background: #e9ecef; border-radius: 999px; height: 8px; overflow: hidden; }
    .bar-fill { display: block; height: 100%; border-radius: 999px; background: #26a69a; }
    .bar-val { font-size: 12px; color: #555; white-space: nowrap; min-width: 40px; text-align: right; }

    .composite { border-color: #26a69a; background: #f6fffd; break-inside: auto; page-break-inside: auto; }
    .composite-desc { font-size: 13px; color: #00695c; line-height: 1.6; background: #e0f2f1; border: 1px solid #b2dfdb; border-radius: 8px; padding: 10px 12px; margin-bottom: 10px; font-weight: 500; break-inside: avoid; page-break-inside: avoid; }
    .composite-summary { font-size: 12px; color: #444; line-height: 1.6; margin: 8px 0; text-align: center; break-inside: avoid; page-break-inside: avoid; }
    .composite-li { font-size: 12px; color: #444; line-height: 1.7; }
    .composite-strategies { margin-bottom: 8px; break-inside: avoid; page-break-inside: avoid; }
    .composite-study { font-size: 12px; color: #00695c; background: #e0f2f1; border: 1px solid #b2dfdb; border-radius: 8px; padding: 8px 10px; line-height: 1.6; break-inside: avoid; page-break-inside: avoid; }
    .radar-wrap { text-align: center; margin: 8px 0 4px; break-inside: avoid; page-break-inside: avoid; }
    .radar-svg { width: 100%; max-width: 320px; height: auto; display: inline-block; }
  </style>
</head>
<body>
  ${bodyContent}
</body>
</html>`;
}